import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ListMusic, Calendar, Share2, Edit, Music, ChevronDown, ChevronUp, Key, User, ArrowLeft, Guitar, Type } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SECTION_LABELS = ['verse', 'chorus', 'bridge', 'pre-chorus', 'prechorus', 'pre chorus',
  'tag', 'outro', 'intro', 'interlude', 'refrain', 'coda', 'vamp', 'ending', 'instrumental', 'repeat'];

function isSectionLabel(line) {
  const s = line.trim();
  if (/^\[.+\]$/.test(s) || /^\(.+\)$/.test(s)) return true;
  const lower = s.toLowerCase().replace(/:$/, '').trim();
  return SECTION_LABELS.some(lbl => lower === lbl || new RegExp(`^${lbl}\\s*\\d*$`).test(lower));
}

function formatSectionLabel(line) {
  return line.trim().replace(/^\[|\]$|\(|\)/g, '').replace(/:$/, '').trim();
}

const MiniLyricsRenderer = ({ content }) => {
  if (!content) return <p className="text-stone-400 text-sm italic">Lyrics not available</p>;
  const lines = content.split('\n');
  return (
    <div className="text-sm text-stone-700 leading-relaxed">
      {lines.map((line, i) => {
        const stripped = line.trim();
        if (isSectionLabel(stripped)) {
          return (
            <div key={i} className="mt-4 mb-1 first:mt-0">
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                {formatSectionLabel(stripped)}
              </span>
            </div>
          );
        }
        if (!stripped) return <div key={i} className="h-3" />;
        return <div key={i}>{stripped}</div>;
      })}
    </div>
  );
};

const PlaylistViewPage = () => {
  const { playlistId } = useParams();
  const navigate = useNavigate();
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSong, setExpandedSong] = useState(null);
  const [viewMode, setViewMode] = useState('lyrics');

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/playlists/${playlistId}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setPlaylist)
      .catch(() => setPlaylist(null))
      .finally(() => setLoading(false));
  }, [playlistId]);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Link copied!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center pt-20" data-testid="playlist-view-loading">
        <div className="w-12 h-12 border-4 border-amber-200 border-t-amber-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!playlist) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center pt-20" data-testid="playlist-not-found">
        <div className="text-center px-4">
          <ListMusic className="w-16 h-16 text-stone-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-stone-800 mb-2">Playlist Not Found</h2>
          <button onClick={() => navigate('/playlists')} className="mt-4 bg-amber-600 text-white px-6 py-3 rounded-lg hover:bg-amber-700 font-medium" data-testid="back-to-playlists-link">
            View All Playlists
          </button>
        </div>
      </div>
    );
  }

  const songs = playlist.songs || [];

  return (
    <div className="min-h-screen bg-stone-50 pt-16" data-testid="playlist-view-page">
      {/* Header */}
      <div className="bg-stone-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <button onClick={() => navigate('/playlists')} className="flex items-center gap-1.5 text-stone-400 hover:text-white text-sm font-medium mb-4 transition-colors" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" /> All Playlists
          </button>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <ListMusic className="w-5 h-5 text-amber-400" />
                <span className="text-amber-400 text-sm font-semibold tracking-widest uppercase">Worship Setlist</span>
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-2" data-testid="playlist-name">
                {playlist.name}
              </h1>
              <div className="flex items-center gap-4 text-stone-400 text-sm">
                {playlist.service_date && (
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    {new Date(playlist.service_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </span>
                )}
                <span>{songs.length} {songs.length === 1 ? 'song' : 'songs'}</span>
              </div>
              {playlist.description && <p className="mt-3 text-stone-400">{playlist.description}</p>}
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button data-testid="share-btn" onClick={handleShare} className="p-2.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all" title="Share">
                <Share2 className="w-4 h-4" />
              </button>
              <button data-testid="edit-btn" onClick={() => navigate(`/playlists/${playlistId}/edit`)} className="p-2.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all" title="Edit">
                <Edit className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Songs */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-3" data-testid="playlist-songs-list">
        {songs.length === 0 ? (
          <div className="bg-white rounded-xl border border-stone-200 p-12 text-center">
            <Music className="w-12 h-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500">This playlist is empty. Tap Edit to add songs.</p>
          </div>
        ) : (
          songs.map((song, idx) => {
            const isExpanded = expandedSong === song.id;
            const hasChords = !!song.lyrics_with_chords;
            const displayContent = viewMode === 'chords' && hasChords ? song.lyrics_with_chords : song.lyrics;

            return (
              <div key={song.id} className="bg-white rounded-xl border border-stone-200 overflow-hidden transition-shadow hover:shadow-sm" data-testid={`view-song-${idx}`}>
                {/* Song header - always visible */}
                <button
                  data-testid={`expand-song-${idx}`}
                  onClick={() => setExpandedSong(isExpanded ? null : song.id)}
                  className="w-full text-left px-5 py-4 flex items-center gap-4"
                >
                  <span className="flex-shrink-0 w-10 h-10 bg-amber-50 text-amber-700 rounded-xl flex items-center justify-center font-bold text-lg">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-stone-800 truncate">{song.title}</h3>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {song.author && (
                        <span className="text-xs text-stone-400 flex items-center gap-1">
                          <User className="w-3 h-3" /> {song.author}
                        </span>
                      )}
                      {song.key_signature && (
                        <span className="text-xs text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded font-medium flex items-center gap-0.5">
                          <Key className="w-3 h-3" /> {song.key_signature}
                        </span>
                      )}
                      {song.year && <span className="text-xs text-stone-400">({song.year})</span>}
                    </div>
                  </div>
                  {isExpanded
                    ? <ChevronUp className="w-5 h-5 text-stone-400 flex-shrink-0" />
                    : <ChevronDown className="w-5 h-5 text-stone-400 flex-shrink-0" />
                  }
                </button>

                {/* Expanded lyrics */}
                {isExpanded && (
                  <div className="border-t border-stone-100 px-5 py-5">
                    {/* View mode toggle if chords available */}
                    {hasChords && (
                      <div className="flex bg-stone-100 rounded-lg p-0.5 w-fit mb-4">
                        <button onClick={() => setViewMode('lyrics')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === 'lyrics' ? 'bg-white shadow-sm text-stone-800' : 'text-stone-500'}`}>
                          <Type className="w-3.5 h-3.5" /> Lyrics
                        </button>
                        <button onClick={() => setViewMode('chords')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === 'chords' ? 'bg-white shadow-sm text-stone-800' : 'text-stone-500'}`}>
                          <Guitar className="w-3.5 h-3.5" /> Chords
                        </button>
                      </div>
                    )}
                    <MiniLyricsRenderer content={displayContent} />
                    <Link
                      to={`/songs/english/${song.id}`}
                      className="inline-block mt-4 text-xs text-amber-600 hover:text-amber-700 font-semibold"
                      data-testid={`view-full-song-${idx}`}
                    >
                      View full details
                    </Link>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PlaylistViewPage;
